import React, { useState, createContext, useContext } from 'react';
type Language = 'en' | 'pl';
interface Translations {
  navbar: {
    about: string;
    services: string;
    testimonials: string;
    contact: string;
    startNow: string;
  };
  hero: {
    badge: string;
    titleLine1: string;
    titleLine2: string;
    titleLine3: string;
    description: string;
    viewPrograms: string;
    meetCoach: string;
  };
  about: {
    photoPlaceholder: string;
    quote: string;
    quoteAuthor: string;
    title: string;
    titleAccent: string;
    paragraph1: string;
    paragraph2: string;
    paragraph2Bold: string;
    yearsExperience: string;
    clientsTransformed: string;
    sessionsCompleted: string;
    cert1: string;
    cert2: string;
    cert3: string;
  };
  services: {
    title: string;
    titleAccent: string;
    subtitle: string;
    mostPopular: string;
    getStarted: string;
    personal: {
      title: string;
      description: string;
      features: string[];
      price: string;
    };
    group: {
      title: string;
      description: string;
      features: string[];
      price: string;
    };
    online: {
      title: string;
      description: string;
      features: string[];
      price: string;
    };
  };
  testimonials: {
    title1: string;
    title2: string;
    subtitle: string;
    happyClients: string;
    items: {
      name: string;
      role: string;
      text: string;
      result: string;
    }[];
  };
  contact: {
    title: string;
    titleAccent: string;
    subtitle: string;
    emailMe: string;
    callMe: string;
    location: string;
    locationAddress: string;
    followMe: string;
    form: {
      fullName: string;
      namePlaceholder: string;
      email: string;
      emailPlaceholder: string;
      phone: string;
      phonePlaceholder: string;
      goals: string;
      goalsPlaceholder: string;
      submit: string;
      disclaimer: string;
    };
  };
  footer: {
    copyright: string;
    privacy: string;
    terms: string;
  };
}
const translations: Record<Language, Translations> = {
  en: {
    navbar: {
      about: 'About',
      services: 'Services',
      testimonials: 'Testimonials',
      contact: 'Contact',
      startNow: 'Start Now'
    },
    hero: {
      badge: 'Certified Personal Trainer',
      titleLine1: 'Transform',
      titleLine2: 'Your Body',
      titleLine3: 'Elevate Your Life',
      description: 'Stop guessing and start progressing. Professional coaching designed to push your limits and unlock your true athletic potential.',
      viewPrograms: 'View Programs',
      meetCoach: 'Meet Coach Alex'
    },
    about: {
      photoPlaceholder: 'Coach Photo Placeholder',
      quote: '"DISCIPLINE IS THE BRIDGE BETWEEN GOALS AND ACCOMPLISHMENT."',
      quoteAuthor: '- Jim Rohn',
      title: 'More Than Just',
      titleAccent: 'Reps & Sets',
      paragraph1: "I'm Alex, a certified strength and conditioning specialist with a passion for human performance. My journey began not in a gym, but on the track, where I learned that true strength comes from consistency, not intensity alone.",
      paragraph2: 'My philosophy is simple:',
      paragraph2Bold: 'Sustainable progress over quick fixes.',
      yearsExperience: 'Years Experience',
      clientsTransformed: 'Clients Transformed',
      sessionsCompleted: 'Sessions Completed',
      cert1: 'NASM Certified Personal Trainer',
      cert2: 'Precision Nutrition Level 1',
      cert3: 'Functional Movement Screen (FMS) Certified'
    },
    services: {
      title: 'Choose Your',
      titleAccent: 'Path',
      subtitle: 'Whether you need hands-on guidance or a structured plan to follow on your own, I have a program designed for your goals.',
      mostPopular: 'Most Popular',
      getStarted: 'Get Started',
      personal: {
        title: '1:1 Personal Training',
        description: 'The ultimate personalized experience. We work together in-person to perfect your form and push your limits safely.',
        features: ['Customized workout plans', 'Real-time form correction', 'Nutritional guidance', '24/7 support access'],
        price: 'Starting at $85/session'
      },
      group: {
        title: 'Small Group Training',
        description: 'High energy sessions with 3-5 people. Get the motivation of a team with the attention of a coach.',
        features: ['Community motivation', 'Structured progression', 'Competitive atmosphere', 'More affordable rate'],
        price: 'Starting at $35/session'
      },
      online: {
        title: 'Online Coaching',
        description: 'Training on your schedule. Get a full program delivered to your phone with weekly check-ins and video feedback.',
        features: ['Workout app access', 'Video form review', 'Weekly check-in calls', 'Macro coaching'],
        price: '$199/month'
      }
    },
    testimonials: {
      title1: 'Real People.',
      title2: 'Real Results.',
      subtitle: "Don't just take my word for it. Here's what my athletes and clients have to say about their journey.",
      happyClients: '500+ Happy Clients',
      items: [{
        name: 'Sarah Jenkins',
        role: 'Marathon Runner',
        text: 'I was stuck at a plateau for 2 years. Within 3 months of training with Alex, I shaved 15 minutes off my marathon time and felt stronger than ever.',
        result: "Lost 12lbs • PR'd Marathon"
      }, {
        name: 'Michael Chen',
        role: 'Software Engineer',
        text: 'The online coaching program was perfect for my busy schedule. The app is easy to use, and the video feedback on my lifts was a game changer for my back pain.',
        result: 'Fixed Back Pain • Gained 8lbs Muscle'
      }, {
        name: 'Jessica Roth',
        role: 'Busy Mom',
        text: 'I was intimidated by the gym, but the small group sessions gave me confidence. The community is so supportive, and I actually look forward to working out now.',
        result: 'Consistent for 6 Months'
      }]
    },
    contact: {
      title: 'Ready to',
      titleAccent: 'Start?',
      subtitle: "Fill out the form to schedule your free consultation call. We'll discuss your goals, history, and find the best plan for you.",
      emailMe: 'Email Me',
      callMe: 'Call Me',
      location: 'Location',
      locationAddress: 'Iron District Gym\n123 Fitness Blvd, Los Angeles, CA',
      followMe: 'Follow My Journey',
      form: {
        fullName: 'Full Name',
        namePlaceholder: 'John Doe',
        email: 'Email Address',
        emailPlaceholder: 'john@example.com',
        phone: 'Phone (Optional)',
        phonePlaceholder: '(555) 123-4567',
        goals: 'Your Goals',
        goalsPlaceholder: 'Tell me about your fitness goals...',
        submit: 'Send Message',
        disclaimer: '*This is a demo form. No data is actually sent.'
      }
    },
    footer: {
      copyright: 'Coach Alex Fitness. All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service'
    }
  },
  pl: {
    navbar: {
      about: 'O Mnie',
      services: 'Usługi',
      testimonials: 'Opinie',
      contact: 'Kontakt',
      startNow: 'Zacznij Teraz'
    },
    hero: {
      badge: 'Certyfikowany Trener Personalny',
      titleLine1: 'Przemień',
      titleLine2: 'Swoje Ciało',
      titleLine3: 'Podnieś Jakość Życia',
      description: 'Przestań zgadywać i zacznij robić postępy. Profesjonalny coaching zaprojektowany, aby przekraczać Twoje granice i uwolnić Twój prawdziwy potencjał.',
      viewPrograms: 'Zobacz Programy',
      meetCoach: 'Poznaj Trenera'
    },
    about: {
      photoPlaceholder: 'Zdjęcie Trenera',
      quote: '"DYSCYPLINA JEST MOSTEM MIĘDZY CELAMI A ICH REALIZACJĄ."',
      quoteAuthor: '- Jim Rohn',
      title: 'Więcej Niż Tylko',
      titleAccent: 'Powtórzenia i Serie',
      paragraph1: 'Jestem Alex, certyfikowany specjalista od siły i kondycji z pasją do ludzkiej wydajności. Moja podróż nie zaczęła się na siłowni, ale na bieżni, gdzie nauczyłem się, że prawdziwa siła pochodzi z konsekwencji, a nie tylko z intensywności.',
      paragraph2: 'Moja filozofia jest prosta:',
      paragraph2Bold: 'Zrównoważony postęp zamiast szybkich rozwiązań.',
      yearsExperience: 'Lat Doświadczenia',
      clientsTransformed: 'Przemienionych Klientów',
      sessionsCompleted: 'Ukończonych Sesji',
      cert1: 'Certyfikowany Trener Personalny NASM',
      cert2: 'Precision Nutrition Poziom 1',
      cert3: 'Certyfikat FMS (Functional Movement Screen)'
    },
    services: {
      title: 'Wybierz Swoją',
      titleAccent: 'Ścieżkę',
      subtitle: 'Niezależnie od tego, czy potrzebujesz bezpośredniego wsparcia, czy ustrukturyzowanego planu do samodzielnej realizacji, mam program dopasowany do Twoich celów.',
      mostPopular: 'Najpopularniejszy',
      getStarted: 'Rozpocznij',
      personal: {
        title: 'Trening Personalny 1:1',
        description: 'Najwyższy poziom spersonalizowanego doświadczenia. Pracujemy razem osobiście, doskonaląc Twoją technikę i bezpiecznie przekraczając granice.',
        features: ['Spersonalizowane plany treningowe', 'Korekta techniki w czasie rzeczywistym', 'Wskazówki żywieniowe', 'Wsparcie 24/7'],
        price: 'Od 350 zł/sesja'
      },
      group: {
        title: 'Trening w Małej Grupie',
        description: 'Energetyczne sesje z 3-5 osobami. Zyskaj motywację zespołu z uwagą trenera.',
        features: ['Motywacja społeczności', 'Ustrukturyzowana progresja', 'Atmosfera rywalizacji', 'Przystępniejsza cena'],
        price: 'Od 150 zł/sesja'
      },
      online: {
        title: 'Coaching Online',
        description: 'Trening według Twojego harmonogramu. Pełny program dostarczony na Twój telefon z cotygodniowymi konsultacjami i analizą wideo.',
        features: ['Dostęp do aplikacji treningowej', 'Analiza wideo techniki', 'Cotygodniowe rozmowy', 'Coaching makro'],
        price: '799 zł/miesiąc'
      }
    },
    testimonials: {
      title1: 'Prawdziwi Ludzie.',
      title2: 'Prawdziwe Rezultaty.',
      subtitle: 'Nie wierz mi na słowo. Oto co moi sportowcy i klienci mówią o swojej podróży.',
      happyClients: '500+ Zadowolonych Klientów',
      items: [{
        name: 'Anna Kowalska',
        role: 'Biegaczka Maratonów',
        text: 'Przez 2 lata stałam w miejscu. W ciągu 3 miesięcy treningu z Alexem skróciłam czas maratonu o 15 minut i czułam się silniejsza niż kiedykolwiek.',
        result: 'Schudła 5kg • Rekord Maratonu'
      }, {
        name: 'Michał Nowak',
        role: 'Programista',
        text: 'Program coachingu online był idealny dla mojego napiętego grafiku. Aplikacja jest łatwa w użyciu, a analiza wideo moich ćwiczeń była przełomem dla mojego bólu pleców.',
        result: 'Koniec z Bólem Pleców • +4kg Mięśni'
      }, {
        name: 'Joanna Wiśniewska',
        role: 'Zapracowana Mama',
        text: 'Bałam się siłowni, ale treningi grupowe dały mi pewność siebie. Społeczność jest bardzo wspierająca i naprawdę cieszę się na każdy trening.',
        result: 'Regularność od 6 Miesięcy'
      }]
    },
    contact: {
      title: 'Gotowy, aby',
      titleAccent: 'Zacząć?',
      subtitle: 'Wypełnij formularz, aby umówić się na bezpłatną konsultację. Omówimy Twoje cele, historię i znajdziemy najlepszy plan dla Ciebie.',
      emailMe: 'Napisz do Mnie',
      callMe: 'Zadzwoń',
      location: 'Lokalizacja',
      locationAddress: 'Iron District Gym\nul. Fitness 123, Warszawa',
      followMe: 'Śledź Moją Podróż',
      form: {
        fullName: 'Imię i Nazwisko',
        namePlaceholder: 'Jan Kowalski',
        email: 'Adres Email',
        emailPlaceholder: 'jan@example.com',
        phone: 'Telefon (Opcjonalnie)',
        phonePlaceholder: '+48 123 456 789',
        goals: 'Twoje Cele',
        goalsPlaceholder: 'Opowiedz mi o swoich celach fitness...',
        submit: 'Wyślij Wiadomość',
        disclaimer: '*To jest formularz demo. Żadne dane nie są wysyłane.'
      }
    },
    footer: {
      copyright: 'Coach Alex Fitness. Wszelkie prawa zastrzeżone.',
      privacy: 'Polityka Prywatności',
      terms: 'Regulamin'
    }
  }
};
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  const value = {
    language,
    setLanguage,
    t: translations[language]
  };
  return <LanguageContext.Provider value={value} data-id="element-198">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}